import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { AudioProvider } from '@/context/AudioContext';
import { CartProvider } from '@/context/CartContext';
import { AuthProvider } from '@/context/AuthContext';
import { MembershipProvider } from '@/context/MembershipContext';
import { VideoProvider } from '@/context/VideoContext';
import VideoOverlay from '@/components/VideoOverlay';
import { Layout } from '@/components/Layout';
import { Home } from '@/pages/Home';
import { MusicStore } from '@/pages/MusicStore';
import { ArtworkStore } from '@/pages/ArtworkStore';
import ClearCache from '@/pages/ClearCache';
import { Cart } from '@/pages/Cart';
import { Checkout } from '@/pages/Checkout';
import { Success } from '@/pages/Success';
import { About } from '@/pages/About';
import { Legacy } from '@/pages/Legacy';
import PayPerView from '@/pages/PayPerView';
import PPVWatch from '@/pages/PPVWatch';
import { Help } from '@/pages/Help';
import { Library } from '@/pages/Library';
import Videos from '@/pages/Videos';
import { VideoWatch } from '@/pages/VideoWatch';
import { Classes } from '@/pages/Classes';
import { PerformanceDetails } from '@/pages/PerformanceDetails';
import { Live } from '@/pages/Live';
import Courses from '@/pages/Courses';
import { Membership } from '@/pages/Membership';
import { DrumRepair } from '@/pages/DrumRepair';
import { SambaLessons } from '@/pages/SambaLessons';
import LiveLessons from '@/pages/LiveLessons';
import { LessonWatch } from '@/pages/LessonWatch';
import { MyLessons } from '@/pages/MyLessons';
import { PandeiroJuggling } from '@/pages/PandeiroJuggling';
import { KapoeiraBook } from '@/pages/KapoeiraBook';
import { MestreBullfrog } from '@/pages/MestreBullfrog';
import PurchaseConfirmation from '@/pages/PurchaseConfirmation';
import MembershipAgreement from '@/pages/MembershipAgreement';
import ScrollToTop from '@/components/ScrollToTop';
import QuickBuyPage from '@/pages/QuickBuy';
import Registration from '@/pages/Registration';
import { Certificates } from '@/pages/Certificates';
import CapoeiraLanding from '@/pages/CapoeiraLanding';
import KapoeiraAngolaSite from '@/pages/KapoeiraAngolaSite';
import MestreFullStory from '@/pages/MestreFullStory';
import MuseumProposal from '@/pages/MuseumProposal';
import AncestralProject from '@/pages/AncestralProject';
import GrantWorkspace from '@/pages/GrantWorkspace';
import AncestorsFlyer from '@/pages/AncestorsFlyer';
import BudgetPrint from '@/pages/BudgetPrint';
import PrivacyPolicy from '@/pages/PrivacyPolicy';
import TermsOfService from '@/pages/TermsOfService';
import { DynamicPage } from '@/components/DynamicPage';
import { AdminLogin } from '@/pages/admin/AdminLogin';
import { CustomerLogin } from '@/pages/CustomerLogin';
import MyStudioProjects from '@/pages/MyStudioProjects';
import { AdminDashboard } from '@/pages/admin/AdminDashboard';
import AdminContentSync from '@/pages/admin/AdminContentSync';
import AdminPaymentSettings from '@/pages/admin/AdminPaymentSettings';
import AdminTrackRepair from '@/pages/admin/AdminTrackRepair';
import { AdminMusic } from '@/pages/admin/AdminMusic';
import { AdminOrders } from '@/pages/admin/AdminOrders';
import { AdminCustomers } from '@/pages/admin/AdminCustomers';
import { AdminSettings } from '@/pages/admin/AdminSettings';
import { AdminTheme } from '@/pages/admin/AdminTheme';
import { AdminMedia } from '@/pages/admin/AdminMedia';
import { AdminPages } from '@/pages/admin/AdminPages';
import { AdminPageEditor } from '@/pages/admin/AdminPageEditor';
import { AdminVideos } from '@/pages/admin/AdminVideos';
import AdminClasses from '@/pages/admin/AdminClasses';
import AdminAnalytics from '@/pages/admin/AdminAnalytics';
import AdminQRCodes from '@/pages/admin/AdminQRCodes';
import AdminUsers from '@/pages/admin/AdminUsers';
import AdminLive from '@/pages/admin/AdminLive';
import AdminSiteContent from '@/pages/admin/AdminSiteContent';
import AdminMemberships from '@/pages/admin/AdminMemberships';
import AdminLiveEditor from '@/pages/admin/AdminLiveEditor';
import AdminVisualEditor from '@/pages/admin/AdminVisualEditor';
import AdminCourseEditor from '@/pages/admin/AdminCourseEditor';
import AdminPandeiroCurriculum from '@/pages/admin/AdminPandeiroCurriculum';
import AdminClassEditor from '@/pages/admin/AdminClassEditor';
import AdminAboutEditor from '@/pages/admin/AdminAboutEditor';
import AdminContactMessages from '@/pages/admin/AdminContactMessages';
import AdminQuoteRequests from '@/pages/admin/AdminQuoteRequests';
import AdminQuoteFormEditor from '@/pages/admin/AdminQuoteFormEditor';
import AdminPricingEditor from '@/pages/admin/AdminPricingEditor';
import AdminPodcasts from '@/pages/admin/AdminPodcasts';
import AdminDownloadRequests from '@/pages/admin/AdminDownloadRequests';
import AdminLiveStudio from '@/pages/admin/AdminLiveStudio';
import AdminMobileStudio from '@/pages/admin/AdminMobileStudio';
import AdminLiveStudioHost from '@/pages/admin/AdminLiveStudioHost';
import Podcasts from '@/pages/Podcasts';
import AdminCasaSambaAboutEditor from '@/pages/admin/AdminCasaSambaAboutEditor';
import AdminPPVEditor from '@/pages/admin/AdminPPVEditor';
import AdminPlayAlong from '@/pages/admin/AdminPlayAlong';
import AdminLiveLessons from '@/pages/admin/AdminLiveLessons';
import AdminBulkWavUpload from '@/pages/admin/AdminBulkWavUpload';
import AdminGallery from '@/pages/admin/AdminGallery';
import HeroSlidesEditor from '@/pages/admin/HeroSlidesEditor';
import AdminPerformances from '@/pages/admin/AdminPerformances';
import FlyerGenerator from '@/pages/admin/FlyerGenerator';
import FlyerPublic from '@/pages/FlyerPublic';
import ReelBuilder from '@/pages/admin/ReelBuilder';
import FlyerGifsUpload from '@/pages/admin/FlyerGifsUpload';
import AdminHomepage from '@/pages/admin/AdminHomepage';
import PreviewCheck from '@/pages/PreviewCheck';
import AdminScoreCreator from '@/pages/admin/AdminScoreCreator';
import AdminSampleManager from '@/pages/admin/AdminSampleManager';
import AdminKitSamples from '@/pages/admin/AdminKitSamples';
import SiteManager from '@/pages/admin/SiteManager';
import StudioDAW from '@/pages/admin/StudioDAW';
import AdminArtwork from '@/pages/admin/AdminArtwork';
import AdminBatucada from '@/pages/admin/AdminBatucada';
import AdminBatucadaNew from '@/pages/admin/AdminBatucadaNew';
import BulkSampleUpload from '@/pages/admin/SimpleBulkUpload';
import AdminBackingTracks from '@/pages/admin/AdminBackingTracks';
import AdminKapoeiraEditor from '@/pages/admin/AdminKapoeiraEditor';
import AdminCostumes from '@/pages/admin/AdminCostumes';
import AdminHeroEditor from '@/pages/admin/AdminHeroEditor';
import AdminGalleryCreator from '@/pages/admin/AdminGalleryCreator';
import GalleryViewer from '@/pages/GalleryViewer';
import ArtworkEnhancer from '@/pages/admin/ArtworkEnhancer';
import LivePageEditor from '@/pages/admin/LivePageEditor';
import LiveSiteEditor from '@/pages/admin/LiveSiteEditor';
import AdminHomeEditor from '@/pages/admin/AdminHomeEditor';
import RecordingStudio from '@/pages/RecordingStudio';
import LiveStudioBooking from '@/pages/LiveStudioBooking';
import AdminRecordingStudio from '@/pages/admin/AdminRecordingStudio';
import LiveRemoteSession from '@/pages/admin/LiveRemoteSession';
import StudioSession from '@/pages/admin/StudioSession';
import JoinSession from '@/pages/JoinSession';
import JoinSessionNew from '@/pages/JoinSessionNew';
import SimpleJoin from '@/pages/SimpleJoin';
import AdminSessionManager from '@/pages/admin/AdminSessionManager';
import HostVideoRoom from '@/pages/admin/HostVideoRoom';
import SimpleVideoRoom from '@/pages/SimpleVideoRoom';
import LiveKitRoom from '@/pages/LiveKitRoom';
import CameraTest from '@/pages/CameraTest';
import CameraTest2 from '@/pages/CameraTest2';
import SampleStore from '@/pages/SampleStore';
import CostumeShop from '@/pages/CostumeShop';
import CostumeDetails from '@/pages/CostumeDetails';
import CostumeOrderConfirmation from '@/pages/CostumeOrderConfirmation';
import Gallery from '@/pages/Gallery';
import BatucadaRio from '@/pages/BatucadaRio';
import BrasilianGrooves from '@/pages/BrasilianGrooves';
import HumToMusic from '@/pages/HumToMusic';
import FaShowRecorder from '@/pages/FaShowRecorder';
import PublicPage from '@/pages/PublicPage';
import Contact from '@/pages/Contact';
import BookingPage, { QuotePage } from '@/pages/BookingPage';
import MultiCamStudio from '@/pages/MultiCamStudio';
import MobileStudio from '@/pages/MobileStudio';
import StudioGallery from '@/pages/StudioGallery';
import LegacyHomeHero from '@/pages/LegacyHomeHero';
import AudioSetup from '@/pages/AudioSetup';
import StudioTest from '@/pages/StudioTest';
import { LiveChat } from '@/components/LiveChat';
import { Toaster } from '@/components/ui/sonner';
import { useContentProtection, contentProtectionStyles } from '@/hooks/useContentProtection';
import { useAuth } from '@/context/AuthContext';
import '@/App.css';

// Content Protection Wrapper Component
// Uses the reactive AuthContext so we never rely on stale sessionStorage
// (Safari clears sessionStorage between tabs which used to leave admins
// stuck behind the public-visitor protection).
const ContentProtectionWrapper = ({ children }) => {
  const { isAdmin: isAdminFn } = useAuth();
  const isAdmin = typeof isAdminFn === 'function' ? isAdminFn() : false;

  // Apply content protection only for non-admins
  useContentProtection(isAdmin);

  return (
    <div className={isAdmin ? '' : 'content-protected'}>
      {!isAdmin && <style>{contentProtectionStyles}</style>}
      {children}
    </div>
  );
};

function App() {
  const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
  return (
    <BrowserRouter>
      <ScrollToTop />
      <GoogleReCaptchaProvider
        reCaptchaKey={recaptchaSiteKey}
        scriptProps={{ async: true, defer: true, appendTo: 'head' }}
        useEnterprise={false}
      >
      <AuthProvider>
      <VideoProvider>
        <MembershipProvider>
          <AudioProvider>
            <CartProvider>
              <ContentProtectionWrapper>
              <Routes>
              {/* Public Routes */}
              <Route path="/" element={<Layout><Home /></Layout>} />
              <Route path="/clear-cache" element={<ClearCache />} />
              <Route path="/music" element={<Layout><MusicStore /></Layout>} />
              <Route path="/artwork" element={<Layout><ArtworkStore /></Layout>} />
              <Route path="/cart" element={<Layout><Cart /></Layout>} />
              <Route path="/checkout" element={<Layout><Checkout /></Layout>} />
              <Route path="/success" element={<Layout><Success /></Layout>} />
              <Route path="/purchase/confirmation" element={<Layout><PurchaseConfirmation /></Layout>} />
              <Route path="/purchase/success" element={<Layout><PurchaseConfirmation /></Layout>} />
              <Route path="/about" element={<Layout><About /></Layout>} />
              <Route path="/legacy" element={<Layout><Legacy /></Layout>} />
              <Route path="/mestre-full-story" element={<Layout><MestreFullStory /></Layout>} />
              <Route path="/p/:slug" element={<PublicPage />} />
              <Route path="/ppv" element={<Layout><PayPerView /></Layout>} />
              <Route path="/ppv/watch" element={<Layout><PPVWatch /></Layout>} />
              <Route path="/ppv/success" element={<Layout><PPVWatch /></Layout>} />
              <Route path="/help" element={<Layout><Help /></Layout>} />
              <Route path="/contact" element={<Layout><Contact /></Layout>} />
              <Route path="/booking" element={<Layout><BookingPage /></Layout>} />
              <Route path="/quote" element={<Layout><QuotePage /></Layout>} />
              <Route path="/get-a-quote" element={<Layout><QuotePage /></Layout>} />
              <Route path="/podcasts" element={<Layout><Podcasts /></Layout>} />
              <Route path="/library" element={<Layout><Library /></Layout>} />
              <Route path="/videos" element={<Layout><Videos /></Layout>} />
              <Route path="/classes" element={<Layout><Classes /></Layout>} />
              <Route path="/performances" element={<Layout><PerformanceDetails /></Layout>} />
              <Route path="/live" element={<Layout><Live /></Layout>} />
              <Route path="/courses" element={<Layout><Courses /></Layout>} />
              <Route path="/courses/success" element={<Layout><Courses /></Layout>} />
              <Route path="/membership" element={<Layout><Membership /></Layout>} />
              <Route path="/membership/success" element={<Layout><Membership /></Layout>} />
              <Route path="/drum-repair" element={<Layout><DrumRepair /></Layout>} />
              <Route path="/recording-studio" element={<Layout><RecordingStudio /></Layout>} />
              <Route path="/my-studio-projects" element={<Layout><MyStudioProjects /></Layout>} />
              <Route path="/live-studio" element={<Layout><LiveStudioBooking /></Layout>} />
              <Route path="/sample-store" element={<Layout><SampleStore /></Layout>} />
              <Route path="/costume-shop" element={<Layout><CostumeShop /></Layout>} />
              <Route path="/costume-shop/:costumeId" element={<Layout><CostumeDetails /></Layout>} />
              <Route path="/costume-shop/order-confirmation" element={<Layout><CostumeOrderConfirmation /></Layout>} />
              <Route path="/gallery" element={<Layout><Gallery /></Layout>} />
              <Route path="/gallery/:albumSlug" element={<Layout><Gallery /></Layout>} />
              <Route path="/batucada" element={<BatucadaRio />} />
              <Route path="/brazilian-grooves" element={<BrasilianGrooves />} />
              <Route path="/hum-to-music" element={<HumToMusic />} />
              <Route path="/you-say-we-play" element={<FaShowRecorder />} />
              <Route path="/join-session" element={<JoinSessionNew />} />
              <Route path="/join-session/:roomCode" element={<JoinSessionNew />} />
              <Route path="/join-session-old" element={<JoinSession />} />
              <Route path="/join-session-old/:roomCode" element={<JoinSession />} />
              <Route path="/join/:sessionCode" element={<JoinSession />} />
              <Route path="/simple-join" element={<SimpleJoin />} />
              <Route path="/simple-join/:roomCode" element={<SimpleJoin />} />
              <Route path="/camera-test" element={<CameraTest />} />
              <Route path="/camera-test-2" element={<CameraTest2 />} />
              <Route path="/video-room" element={<SimpleVideoRoom />} />
              <Route path="/video-room/:roomCode" element={<SimpleVideoRoom />} />
              <Route path="/video-room/:roomCode/host" element={<SimpleVideoRoom />} />
              <Route path="/livekit-room" element={<LiveKitRoom />} />
              <Route path="/livekit-room/:roomCode" element={<LiveKitRoom />} />
              <Route path="/livekit-room/:roomCode/host" element={<LiveKitRoom />} />
              <Route path="/drum-repair/success" element={<Layout><DrumRepair /></Layout>} />
              <Route path="/samba-lessons" element={<Layout><SambaLessons /></Layout>} />
              <Route path="/samba-lessons/success" element={<Layout><SambaLessons /></Layout>} />
              <Route path="/play-along" element={<Layout><LiveLessons /></Layout>} />
              <Route path="/live-lessons" element={<Layout><LiveLessons /></Layout>} />
              <Route path="/live-lessons/join/:sessionCode" element={<Layout><LiveLessons /></Layout>} />
              <Route path="/lesson/:lessonId" element={<Layout><LessonWatch /></Layout>} />
              <Route path="/my-lessons" element={<Layout><MyLessons /></Layout>} />
              <Route path="/pandeiro-juggling" element={<Layout><PandeiroJuggling /></Layout>} />
              <Route path="/kapoeira-book" element={<Layout><KapoeiraBook /></Layout>} />
              <Route path="/kapoeira-book/success" element={<Layout><KapoeiraBook /></Layout>} />
              <Route path="/mestre-bullfrog" element={<Layout><MestreBullfrog /></Layout>} />
              <Route path="/membership-agreement" element={<Layout><MembershipAgreement /></Layout>} />
              <Route path="/capoeira" element={<Layout><CapoeiraLanding /></Layout>} />
              <Route path="/kapoeira-angola" element={<Layout><KapoeiraAngolaSite /></Layout>} />
              <Route path="/privacy-policy" element={<Layout><PrivacyPolicy /></Layout>} />
              <Route path="/terms-of-service" element={<Layout><TermsOfService /></Layout>} />
              <Route path="/certificates" element={<Layout><Certificates /></Layout>} />
              
              {/* Museum Proposal */}
              <Route path="/museum-proposal" element={<MuseumProposal />} />
              <Route path="/ancestral-project" element={<AncestralProject />} />
              <Route path="/grant-workspace" element={<GrantWorkspace />} />
              <Route path="/ancestors-flyer" element={<AncestorsFlyer />} />
              <Route path="/ancestral-project/budget/:tierId" element={<BudgetPrint />} />
              
              {/* Quick Buy - QR Code instant purchase */}
              <Route path="/quick-buy/:type/:id" element={<QuickBuyPage />} />
              
              <Route path="/watch/:courseId" element={<Layout><VideoWatch /></Layout>} />
              <Route path="/watch/:courseId/:videoId" element={<Layout><VideoWatch /></Layout>} />
              
              {/* Dynamic CMS Pages */}
              <Route path="/page/:slug" element={<Layout><DynamicPage /></Layout>} />
              
              {/* Customer sign-in / registration */}
              <Route path="/login" element={<Layout><CustomerLogin /></Layout>} />
              <Route path="/register" element={<Layout><CustomerLogin /></Layout>} />
              <Route path="/registration" element={<Layout><Registration /></Layout>} />
              <Route path="/carnaval" element={<Layout><Registration /></Layout>} />
              <Route path="/mardi-gras-brasil-2026" element={<Layout><Registration /></Layout>} />
              
              {/* Admin Routes */}
              <Route path="/admin" element={<AdminLogin />} />
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin/dashboard" element={<AdminDashboard />} />
              <Route path="/admin/content-sync" element={<AdminContentSync />} />
              <Route path="/admin/payment-settings" element={<AdminPaymentSettings />} />
              <Route path="/admin/track-repair" element={<AdminTrackRepair />} />
              <Route path="/admin/music" element={<AdminMusic />} />
              <Route path="/admin/music/bulk-wav" element={<AdminBulkWavUpload />} />
              <Route path="/admin/brazilian-grooves" element={<AdminBatucadaNew />} />
              <Route path="/admin/brazilian-grooves/bulk-upload" element={<BulkSampleUpload />} />
              <Route path="/admin/backing-tracks" element={<AdminBackingTracks />} />
              <Route path="/admin/batucada" element={<AdminBatucada />} />
              <Route path="/admin/batucada-new" element={<AdminBatucadaNew />} />
              <Route path="/admin/orders" element={<AdminOrders />} />
              <Route path="/admin/customers" element={<AdminCustomers />} />
              <Route path="/admin/settings" element={<AdminSettings />} />
              <Route path="/admin/theme" element={<AdminTheme />} />
              <Route path="/admin/media" element={<AdminMedia />} />
              <Route path="/admin/pages" element={<AdminPages />} />
              <Route path="/admin/pages/new" element={<AdminPageEditor />} />
              <Route path="/admin/pages/edit/:id" element={<AdminPageEditor />} />
              <Route path="/admin/site-content" element={<AdminSiteContent />} />
              <Route path="/admin/live-editor" element={<AdminLiveEditor />} />
              <Route path="/admin/live-editor/:pageId" element={<AdminLiveEditor />} />
              <Route path="/admin/visual-editor" element={<AdminVisualEditor />} />
              <Route path="/admin/course-editor" element={<AdminCourseEditor />} />
              <Route path="/admin/pandeiro-curriculum" element={<AdminPandeiroCurriculum />} />
              <Route path="/admin/class-editor" element={<AdminClassEditor />} />
              <Route path="/admin/about-editor" element={<AdminAboutEditor />} />
              <Route path="/admin/contact-messages" element={<AdminContactMessages />} />
              <Route path="/admin/quote-requests" element={<AdminQuoteRequests />} />
              <Route path="/admin/quote-form-editor" element={<AdminQuoteFormEditor />} />
              <Route path="/admin/pricing-editor" element={<AdminPricingEditor />} />
              <Route path="/admin/podcasts" element={<AdminPodcasts />} />
              <Route path="/admin/download-requests" element={<AdminDownloadRequests />} />
              <Route path="/admin/live-studio" element={<AdminLiveStudio />} />
              <Route path="/admin/live-studio/host" element={<HostVideoRoom />} />
              <Route path="/admin/live-studio/sessions" element={<AdminSessionManager />} />
              <Route path="/admin/home-editor" element={<AdminHomeEditor />} />
              <Route path="/admin/casa-samba-about-editor" element={<AdminCasaSambaAboutEditor />} />
              <Route path="/admin/ppv-editor" element={<AdminPPVEditor />} />
              <Route path="/admin/play-along-editor" element={<AdminPlayAlong />} />
              <Route path="/admin/live-lessons" element={<AdminLiveLessons />} />
              <Route path="/admin/gallery" element={<AdminGallery />} />
              <Route path="/admin/hero-slides" element={<HeroSlidesEditor />} />
              <Route path="/admin/performances" element={<AdminPerformances />} />
              <Route path="/admin/flyer" element={<FlyerGenerator />} />
              <Route path="/admin/reel" element={<ReelBuilder />} />
              <Route path="/admin/flyer-gifs" element={<FlyerGifsUpload />} />
              <Route path="/flyer" element={<Layout><FlyerPublic /></Layout>} />
              <Route path="/admin/homepage" element={<AdminHomepage />} />
              <Route path="/preview-check" element={<PreviewCheck />} />
              <Route path="/admin/kapoeira-editor" element={<AdminKapoeiraEditor />} />
              <Route path="/admin/costumes" element={<AdminCostumes />} />
              <Route path="/admin/hero-editor" element={<AdminHeroEditor />} />
              <Route path="/admin/gallery-creator" element={<AdminGalleryCreator />} />
              <Route path="/g/:slug" element={<GalleryViewer />} />
              <Route path="/admin/score-creator" element={<AdminScoreCreator />} />
              <Route path="/admin/kit-samples" element={<AdminKitSamples />} />
              <Route path="/admin/studio" element={<StudioDAW />} />
              <Route path="/admin/sample-manager" element={<AdminSampleManager />} />
              <Route path="/admin/artwork-editor" element={<AdminArtwork />} />
              <Route path="/admin/artwork-enhancer" element={<ArtworkEnhancer />} />
              <Route path="/admin/page-builder" element={<LivePageEditor />} />
              <Route path="/admin/site-editor" element={<LiveSiteEditor />} />
              <Route path="/admin/site-manager" element={<SiteManager />} />
              <Route path="/admin/videos" element={<AdminVideos />} />
              <Route path="/admin/classes" element={<AdminClasses />} />
              <Route path="/admin/analytics" element={<AdminAnalytics />} />
              <Route path="/admin/qr-codes" element={<AdminQRCodes />} />
              <Route path="/admin/users" element={<AdminUsers />} />
              <Route path="/admin/live" element={<AdminLive />} />
              <Route path="/admin/memberships" element={<AdminMemberships />} />
              <Route path="/admin/recording-studio" element={<AdminRecordingStudio />} />
              <Route path="/admin/mobile-studio" element={<AdminMobileStudio />} />
              <Route path="/admin/remote-session" element={<LiveRemoteSession />} />
              <Route path="/admin/studio-session" element={<StudioSession />} />
              <Route path="/admin/studio-session/:roomCode" element={<StudioSession />} />
              <Route path="/multi-cam-studio" element={<MultiCamStudio />} />
              <Route path="/mobile-studio" element={<MobileStudio />} />
              <Route path="/audio-setup" element={<AudioSetup />} />
              <Route path="/studio-test" element={<StudioTest />} />
              <Route path="/studio-gallery" element={<StudioGallery />} />
              <Route path="/legacy-home-hero" element={<LegacyHomeHero />} />
            </Routes>
            <LiveChat />
            <VideoOverlay />
            <Toaster position="top-right" richColors />
              </ContentProtectionWrapper>
            </CartProvider>
          </AudioProvider>
        </MembershipProvider>
      </VideoProvider>
      </AuthProvider>
      </GoogleReCaptchaProvider>
    </BrowserRouter>
  );
}

export default App;
